// Section 18 and 22 Policies tab: all 23 policies as a grouped form, each control with its
// "affects next season" preview (the policy def's `affects` hook via policyPreview), the
// one-time trust cost of the change, validation (execution locked) and the Ledger's record of
// past changes.
import {
  catalog,
  policyPreview,
  type Config,
  type Decision,
  type GameState,
  type JusticeResponse,
  type PolicyId,
  type Policies,
  type PreviewLine,
  type SchoolSubject,
} from '../sim/api';
import { stampOfTurn, type SeasonStamp } from './calendar';
import { memo } from './memo';

export type PolicyControlKind = 'choice' | 'toggle' | 'priority' | 'subjects' | 'housing' | 'justice';

export interface PolicyOptionVM {
  value: unknown;
  /** Stable string key (JSON of the value) for option lookups in ui/text/en.ts. */
  key: string;
  /** Trust change for everyone if switched to this option now (<= 0). */
  changeCost: number;
  /** Why this option cannot be chosen now, or null. */
  locked: string | null;
}

export interface PolicyControlVM {
  id: PolicyId;
  label: string;
  group: string;
  kind: PolicyControlKind;
  options: PolicyOptionVM[];
  standing: unknown;
  drafted: unknown;
  changed: boolean;
  /** Who the drafted value affects next season. */
  preview: readonly PreviewLine[];
  /** One-time trust cost of the drafted change. */
  changeCost: number;
  problem: string | null;
  history: (SeasonStamp & { value: string })[];
}

export interface PoliciesVM {
  groups: { id: string; policies: PolicyControlVM[] }[];
  changed: number;
  /** Subjects that may be taught (Section 11 and 18: any two job skills plus literacy). */
  subjects: SchoolSubject[];
  justiceResponses: JusticeResponse[];
  housingModes: Policies['housing']['mode'][];
}

const GROUP_ORDER = ['rations', 'health', 'work', 'housing', 'education', 'society', 'justice'];
const JUSTICE: JusticeResponse[] = [
  'pardon',
  'restitution',
  'imprisonment',
  'flogging',
  'exile',
  'execution',
];
const HOUSING: Policies['housing']['mode'][] = [
  'families-first',
  'workers-first',
  'first-come',
  'manual',
];

function kindOf(id: PolicyId, options: readonly unknown[] | null): PolicyControlKind {
  if (id === 'foodPriority') return 'priority';
  if (id === 'schoolSubjects') return 'subjects';
  if (id === 'housing') return 'housing';
  if (id === 'justice') return 'justice';
  return options !== null && options.every((o) => typeof o === 'boolean') ? 'toggle' : 'choice';
}

type AnyPreview = (
  state: GameState,
  draft: Pick<Decision, 'policies'>,
  id: PolicyId,
  option: unknown,
  cfg?: Config,
) => ReturnType<typeof policyPreview>;
const preview = policyPreview as unknown as AnyPreview;

function optionsFor(
  game: GameState,
  draft: Pick<Decision, 'policies'>,
  id: PolicyId,
  kind: PolicyControlKind,
  options: readonly unknown[] | null,
  drafted: unknown,
  cfg?: Config,
): PolicyOptionVM[] {
  let values: readonly unknown[] = options ?? [];
  if (kind === 'justice') {
    const ask = (drafted as Policies['justice']).askEachCase;
    values = JUSTICE.map((response) => ({ response, askEachCase: ask }));
  } else if (kind === 'housing') {
    const over = (drafted as Policies['housing']).allowOvercrowding;
    values = HOUSING.map((mode) => ({ mode, allowOvercrowding: over }));
  }
  return values.map((value) => {
    const p = preview(game, draft, id, value, cfg);
    return {
      value,
      key: JSON.stringify(value),
      changeCost: p.changeCost,
      locked: p.problem?.message ?? null,
    };
  });
}

export const policiesVM = memo(
  (game: GameState, draft: Decision | null, cfg?: Config): PoliciesVM => {
    const d: Pick<Decision, 'policies'> = { policies: draft?.policies ?? {} };
    const controls = catalog().policies.map((def): PolicyControlVM => {
      const standing = game.policies[def.id] as unknown;
      const drafted = (d.policies[def.id] as unknown) ?? standing;
      const kind = kindOf(def.id, def.options);
      const p = preview(game, d, def.id, drafted, cfg);
      return {
        id: def.id,
        label: def.label,
        group: def.group,
        kind,
        options: optionsFor(game, d, def.id, kind, def.options, drafted, cfg),
        standing,
        drafted,
        changed: JSON.stringify(standing) !== JSON.stringify(drafted),
        preview: p.lines,
        changeCost: JSON.stringify(standing) === JSON.stringify(drafted) ? 0 : p.changeCost,
        problem: p.problem?.message ?? null,
        history: game.ledger.policyChanges
          .filter((c) => c.policyId === def.id)
          .map((c) => ({ ...stampOfTurn(c.turn), value: c.value })),
      };
    });
    const groups = [...new Set(controls.map((c) => c.group))]
      .sort((a, b) => {
        const ia = GROUP_ORDER.indexOf(a);
        const ib = GROUP_ORDER.indexOf(b);
        return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
      })
      .map((id) => ({ id, policies: controls.filter((c) => c.group === id) }));
    const skills = catalog()
      .jobs.map((j) => j.id)
      .filter((j) => j !== 'rest') as SchoolSubject[];
    return {
      groups,
      changed: controls.filter((c) => c.changed).length,
      subjects: [...skills, 'literacy'],
      justiceResponses: JUSTICE,
      housingModes: HOUSING,
    };
  },
);
